import { cn } from '@core/utils';
import type { HomeRecentGiftProductDto } from '@generated/unauth';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import { BREAKPOINTS } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';

import { RecentOrderGiftListItem } from './RecentOrderGiftListItem';
import { RecentOrderGiftListItemLoading } from './RecentOrderGiftListItemLoading';

interface RecentOrderGiftListProps {
  isLoading: boolean;
  listItems: HomeRecentGiftProductDto[][];
  onSlideChange?: (index: number) => void;
}

export const RecentOrderGiftList = ({
  isLoading,
  listItems,
  onSlideChange,
}: RecentOrderGiftListProps) => {
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP);
  const { ref: inViewRef, inView } = useInView({ threshold: 0.3 });

  // Embla Carousel 설정
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      axis: isDesktopViewport ? 'y' : 'x',
      align: 'start',
    },
    [Autoplay({ delay: 3000, stopOnInteraction: false })],
  );

  //* 뷰포트 진입 시에만 autoplay 활성화
  useEffect(() => {
    if (!emblaApi) return;

    try {
      const autoplay = emblaApi.plugins()?.autoplay;
      if (!autoplay) return;

      if (inView) {
        autoplay.play();
      } else {
        autoplay.stop();
      }
    } catch {
      // playOnInit이 초기화 완료 후 자동 재생하므로 무시
    }
  }, [inView, emblaApi]);

  // 슬라이드 변경 이벤트 리스너
  useEffect(() => {
    if (!emblaApi) return;

    const handleSelect = () => {
      const index = emblaApi.selectedScrollSnap();
      onSlideChange?.(index);
    };

    emblaApi.on('select', handleSelect);

    return () => {
      emblaApi.off('select', handleSelect);
    };
  }, [emblaApi, onSlideChange]);

  return (
    <div
      ref={inViewRef}
      className={cn(
        'w-full rounded-[32px] border border-O-300 bg-Gray-white px-32 py-28',
        'mobile-tablet:border-none mobile-tablet:p-0',
      )}
    >
      {isLoading ? (
        <div
          className={cn(
            'h-140 w-full',
            'mobile-tablet:!h-fit mobile-tablet:px-20',
          )}
        >
          <ul
            className={cn(
              'grid grid-cols-3 gap-x-40',
              'mobile-tablet:flex mobile-tablet:flex-col mobile-tablet:gap-16',
            )}
          >
            <RecentOrderGiftListItemLoading />
            <RecentOrderGiftListItemLoading />
            <RecentOrderGiftListItemLoading />
          </ul>
        </div>
      ) : (
        <div
          className={cn(
            'w-full overflow-hidden',
            isDesktopViewport ? 'h-140' : 'h-fit px-20',
            'mobile-tablet:!h-fit mobile-tablet:px-20',
          )}
          ref={emblaRef}
        >
          <div
            className={cn(
              'flex',
              isDesktopViewport ? 'h-full flex-col' : 'flex-row',
            )}
          >
            {listItems.map((chunk, chunkIndex) => (
              <div
                key={chunkIndex}
                role="button"
                tabIndex={0}
                className={cn(
                  isDesktopViewport
                    ? 'mb-12 min-h-0 flex-[0_0_100%]'
                    : 'mr-32 min-w-0 flex-[0_0_335px] last:mr-0',
                )}
                onClick={() => {
                  emblaApi?.plugins()?.autoplay?.stop();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    emblaApi?.plugins()?.autoplay?.stop();
                  }
                }}
              >
                <ul
                  className={cn(
                    'grid grid-cols-3 gap-x-40',
                    'mobile-tablet:flex mobile-tablet:flex-col mobile-tablet:gap-16',
                  )}
                >
                  {chunk.map(
                    (
                      {
                        mainImageUrl: main_image_url,
                        institutionLogoUrl: institution_logo_url,
                        institutionName: institution_name,
                        institutionId: institution_id,
                        productNumber: product_number,
                        productName: product_name,
                        reviewCount: review_count,
                        reviewRating: review_rating,
                        createAt: create_at,
                      },
                      index,
                    ) => (
                      <RecentOrderGiftListItem
                        key={`${chunkIndex}-${index}`}
                        mainImageUrl={main_image_url}
                        institution={{
                          logoUrl: institution_logo_url,
                          id: institution_id,
                          name: institution_name,
                        }}
                        productNumber={product_number}
                        productName={product_name}
                        reviewCount={review_count}
                        reviewRating={review_rating}
                        createAt={create_at}
                      />
                    ),
                  )}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
